import { useState } from "react";
import { Clock, PlayCircle, Lock, Sparkles } from "lucide-react";

function toEmbedUrl(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        // YouTube
        if (u.hostname.includes("youtube.com")) {
            const v = u.searchParams.get("v");
            if (v) return `https://www.youtube.com/embed/${v}`;
        }
        if (u.hostname === "youtu.be") {
            return `https://www.youtube.com/embed${u.pathname}`;
        }
        // Vimeo
        if (u.hostname.includes("vimeo.com")) {
            const id = u.pathname.split("/").filter(Boolean).pop();
            return `https://player.vimeo.com/video/${id}`;
        }
        return url; // assume already an embeddable URL
    } catch {
        return url;
    }
}

export default function LessonsList({ lessons, access }) {
    const [openIdx, setOpenIdx] = useState(null);
    if (!lessons || lessons.length === 0) return null;

    return (
        <ul className="mt-8 space-y-3" data-testid="course-lessons">
            {lessons.map((l, idx) => {
                const hasVideo = Boolean(l.video_url);
                const locked = !hasVideo && !l.is_preview && !access?.enrolled && !access?.member && !access?.admin;
                const isOpen = openIdx === idx;
                return (
                    <li
                        key={l.id || idx}
                        className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden"
                        data-testid={`lesson-${l.id || idx}`}
                    >
                        <button
                            type="button"
                            onClick={() => hasVideo && setOpenIdx(isOpen ? null : idx)}
                            className={`w-full p-5 flex items-start gap-4 text-left hover:border-brand-300 transition ${hasVideo ? "cursor-pointer" : "cursor-default"}`}
                            data-testid={`lesson-toggle-${idx}`}
                        >
                            <span className="h-10 w-10 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center shrink-0 font-heading font-semibold">
                                {String(idx + 1).padStart(2, "0")}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <h3 className="font-heading text-base md:text-lg font-semibold text-ink-900">
                                        {l.title}
                                        {l.is_preview && <span className="ml-2 text-[10px] uppercase tracking-wide rounded-full bg-accent-50 text-accent-700 px-2 py-0.5">free preview</span>}
                                    </h3>
                                    {l.duration && (
                                        <span className="text-xs text-ink-700 inline-flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5" />
                                            {l.duration}
                                        </span>
                                    )}
                                </div>
                                {l.description && (
                                    <p className="mt-2 text-sm text-ink-700 leading-relaxed">
                                        {l.description}
                                    </p>
                                )}
                            </div>
                            {hasVideo ? (
                                <PlayCircle className={`h-6 w-6 shrink-0 ${isOpen ? "text-brand-600" : "text-brand-400"}`} data-testid={`lesson-play-${idx}`} />
                            ) : locked ? (
                                <Lock className="h-5 w-5 text-ink-400 shrink-0" data-testid={`lesson-locked-${idx}`} />
                            ) : (
                                <Sparkles className="h-5 w-5 text-ink-400 shrink-0" />
                            )}
                        </button>
                        {hasVideo && isOpen && (
                            <div className="bg-black aspect-video w-full" data-testid={`lesson-player-${idx}`}>
                                <iframe
                                    src={toEmbedUrl(l.video_url)}
                                    title={l.title}
                                    className="w-full h-full"
                                    allowFullScreen
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
